import { ReportMetadata, ServiceItem, ServiceTransaction, UserProfile } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { autoArchiveVerdict } from './projectAutoArchive';
import { istUebungsprojekt } from './uebung/uebungService';
import { pruefeArchivierbarkeitFuerStand } from './archiveGuardService';
import {
    WAIVER_REFERENCE_PREFIX,
    WaiverPlan,
    planWaiver,
    planWaiverRelease,
} from './serviceWaiverLogic';

/**
 * Schreibpfad des VERZICHTS — die einzige Stelle, die ihn persistiert.
 *
 * Die REGELN liegen daneben in `serviceWaiverLogic.ts` und sind ohne Supabase
 * pruefbar; hier steht nur, was ohne Datenbank nicht zu haben ist. Dasselbe
 * Muster wie projectDeletionLogic/projectDeletionService.
 *
 * Geschrieben wird ueber `saveProjectToCloud`, nicht per direktem UPDATE auf
 * `project_services`. Der Grund ist nicht Bequemlichkeit: die Position lebt in
 * ZWEI Quellen (relational `project_services` und die JSON-Spiegelung in
 * `data->metadata->globalData->services`), und `getNormalizedServices`
 * bevorzugt die relationale und faellt still auf das JSON zurueck. Bliebe eine
 * stehen, spraenge dieselbe Zelle je Ladepfad zwischen zwei Werten — genau die
 * Klasse Befund, die dieses Repo schon mehrfach mit Reparatur-SQL aufloesen
 * musste. `saveProjectToCloud` schreibt beide.
 */

const actorNameOf = (user: UserProfile | null | undefined): string =>
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Buchhaltung' : 'Buchhaltung';

/** Referenz der Protokollspur. Der Zufallsteil trennt zwei Vorgaenge am selben Tag. */
/**
 * Nach dem Schliessen der letzten offenen Menge das Projekt selbst ins Archiv
 * setzen — die Regeln dafuer stehen in projectAutoArchive und sind streng
 * (siehe dort: lieber gar nicht als zu frueh).
 */
const withAutoArchive = async (
    projectId: string,
    globalData: any,
    services: ServiceItem[],
    serviceLog: ServiceTransaction[],
    now: string,
    actor: string,
): Promise<{ globalData: any; notes: any[]; hinweis: string }> => {
    // Uebungsprojekt (Lernzentrum 8.2): kein Auto-Archiv. Das Kennzeichen steht
    // im Blob, weil dieser Pfad die Projektzeile nicht liest.
    const verdict = autoArchiveVerdict(services, globalData?.status, istUebungsprojekt({ istUebung: globalData?.istUebung }));
    if (!verdict.archive) {
        return { globalData, notes: [], hinweis: verdict.reason ? ` Nicht archiviert: ${verdict.reason}` : '' };
    }
    // ARCHIV-RIEGEL: der dritte Weg ins Archiv — und der einzige ohne
    // Anwenderklick. Ein Projekt mit eigenem offenem Posten bleibt auch hier
    // offen, und eine Mutter mit offenen Toechtern ebenfalls.
    // Gerade hier ist der Riegel noetig: "am LV der Mutter ist nichts mehr
    // offen" ist bei einer laufenden Tochter der NORMALZUSTAND zwischen zwei
    // Rechnungen. Ohne ihn setzte sich die Mutter genau in dem Moment selbst
    // ins Archiv, in dem die Buchhaltung die letzte offene Menge schliesst.
    //
    // Geprueft wird der STAND, DER GLEICH GESPEICHERT WIRD, nicht die
    // Datenbank: die traegt hier noch den Zustand von VOR dem Vorgang, und der
    // Riegel hielte dem Anwender die soeben geschlossene Menge als offen vor —
    // das Auto-Archiv griffe nie (Begruendung an
    // pruefeArchivierbarkeitFuerStand).
    //
    // Der Grund wird durchgereicht statt verschwiegen: "nichts passiert" ist
    // bei einem Automatismus die schlechteste Auskunft (siehe
    // AutoArchiveVerdict.reason). Die Regel steht in archiveGuardLogic.ts.
    const urteil = await pruefeArchivierbarkeitFuerStand(projectId, services, serviceLog);
    if (!urteil.erlaubt) {
        return { globalData, notes: [], hinweis: `\n\n${urteil.meldung}` };
    }
    return {
        globalData: { ...globalData, status: 'completed' },
        notes: [
            {
                id: crypto.randomUUID(),
                text: 'Projekt automatisch auf „Beendet" gesetzt: es ist nichts mehr abrechenbar und keine beauftragte Leistung mehr offen. Ins Archiv geht es, sobald die Schlussrechnung gestellt und bezahlt ist.',
                date: now,
                author: actor,
            },
        ],
        hinweis: ' Das Projekt wurde automatisch auf „Beendet" gesetzt.',
    };
};

const newReference = (): string =>
    `${WAIVER_REFERENCE_PREFIX}${new Date().getFullYear()}-${Math.floor(Math.random() * 100000)
        .toString()
        .padStart(5, '0')}`;

export interface WaiverCommand {
    projectId: string;
    serviceId: string;
    /** Menge, auf die zusaetzlich verzichtet wird. Beim Aufheben ohne Bedeutung. */
    quantity?: number;
    /**
     * Die Protokolleintraege, die der Verzicht meint (ServiceItem.waivedEntryIds).
     * Optional: bei Stunden oder einer Teilmenge ohne eigenen Eintrag gibt es
     * keinen Termin zu benennen. Beim Aufheben ohne Bedeutung.
     */
    entryIds?: string[];
    reason: string;
    user: UserProfile | null;
}

export interface WaiverOutcome {
    ok: boolean;
    /** Fuer die Oberflaeche: was schiefging bzw. was geschah. */
    message: string;
}

/**
 * Gemeinsamer Rumpf von Setzen und Aufheben.
 *
 * Frisch geladen wird IMMER: die Liste in der Buchhaltungsansicht ist ein
 * Abzug vom letzten Refresh, und der Verzicht rechnet gegen `quantityUsed` und
 * `quantityBilled`. Auf einem veralteten Stand koennte er eine Menge
 * schliessen, die inzwischen abgerechnet wurde.
 */
const runWaiverCommand = async (
    command: WaiverCommand,
    build: (
        service: ServiceItem,
        ctx: { now: string; entryId: string; reference: string },
        log: ServiceTransaction[],
    ) => ReturnType<typeof planWaiver>,
): Promise<WaiverOutcome> => {
    let fullProject: any;
    try {
        fullProject = await loadProjectById(command.projectId);
    } catch (e: any) {
        return { ok: false, message: `Projekt konnte nicht geladen werden: ${e?.message || e}` };
    }
    if (!fullProject) return { ok: false, message: 'Projekt nicht gefunden.' };

    const meta = fullProject.data?.metadata as ReportMetadata | undefined;
    const services: ServiceItem[] = meta?.globalData?.services || [];
    const target = services.find((s) => s.id === command.serviceId);
    if (!meta || !target) {
        return { ok: false, message: 'Die Position wurde im Projekt nicht gefunden.' };
    }

    const now = new Date().toISOString();
    // Dasselbe Protokoll, das gleich fortgeschrieben wird: die Zuordnung der
    // benannten Eintraege wird gegen den FRISCH geladenen Stand geprueft, nicht
    // gegen den Abzug, den der Dialog anzeigt.
    const log: ServiceTransaction[] = meta.globalData?.serviceLog || [];
    const result = build(target, { now, entryId: crypto.randomUUID(), reference: newReference() }, log);
    // Der Fehlerfall ist hier ABSICHTLICH laut und ohne Schreibvorgang: still
    // zu scheitern ist bei einem Verzicht besonders teuer, weil der Anwender
    // sonst glaubt, den Fall erledigt zu haben, und die Position beim naechsten
    // Rechnungslauf wieder auftaucht.
    if (!result.ok || !result.plan) {
        return { ok: false, message: result.error || 'Der Verzicht konnte nicht gebucht werden.' };
    }

    const plan: WaiverPlan = result.plan;
    const updatedServices = services.map((s) => (s.id === target.id ? plan.service : s));
    const note = {
        id: crypto.randomUUID(),
        text: plan.noteText,
        date: now,
        author: actorNameOf(command.user),
    };

    // EINMAL gebaut und fuer Riegel UND Speichern benutzt: der Archiv-Riegel
    // muss exakt das Protokoll sehen, das gleich in der Datenbank steht.
    const neuesLog: ServiceTransaction[] = [plan.entry, ...(meta.globalData?.serviceLog || [])];
    const archiv = await withAutoArchive(fullProject.id, meta.globalData, updatedServices, neuesLog, now, actorNameOf(command.user));

    try {
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...archiv.globalData,
                        services: updatedServices,
                        serviceLog: neuesLog,
                        notes: [...archiv.notes, note, ...(meta.globalData?.notes || [])],
                    },
                },
            },
            fullProject.id,
        );
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || e}` };
    }

    // Zweitschrift ins Journal — erst nach dem BESTAETIGTEN Speichern und
    // ausserhalb des try darueber: das Anhaengen darf den Ausgang dieses
    // Vorgangs nicht mehr aendern. Das Aufheben laeuft durch denselben Rumpf
    // und haengt ebenfalls nur an (Gegeneintrag), es entfernt nichts.
    await mirrorEntriesToJournal({
        projectId: fullProject.id,
        entries: [plan.entry],
        previousLog: getServiceLog({ metadata: meta }),
        source: 'waiver',
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    });

    return { ok: true, message: plan.noteText + archiv.hinweis };
};

/** Auf eine erbrachte, nicht abgerechnete Menge verzichten. */
export const waiveServiceQuantity = (command: WaiverCommand): Promise<WaiverOutcome> =>
    runWaiverCommand(command, (service, ctx, log) =>
        planWaiver(
            service,
            {
                quantity: command.quantity ?? 0,
                entryIds: command.entryIds,
                reason: command.reason,
                actorName: actorNameOf(command.user),
                actorId: command.user?.id,
                ...ctx,
            },
            log,
        ),
    );

/** Einen Verzicht wieder aufheben — die Menge steht danach erneut zur Abrechnung. */
export const releaseServiceWaiver = (command: WaiverCommand): Promise<WaiverOutcome> =>
    runWaiverCommand(command, (service, ctx) =>
        planWaiverRelease(service, {
            reason: command.reason,
            actorName: actorNameOf(command.user),
            actorId: command.user?.id,
            ...ctx,
        }),
    );
